import { Link } from "react-router-dom";
import { BuildingApartment, Lock } from "@phosphor-icons/react";
import { applyMask } from "shared/utils/masks";
import { Checkbox } from "components/ui/checkbox";

/**
 * Simple LRU cache for memoizing cell configurations
 * Prevents unnecessary re-renders when selection changes
 */
class CellCache {
  private cache = new Map<string, any>();
  private maxSize = 100; // Limit cache to prevent memory leaks

  get(key: string): any | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: string, value: any): void {
    // Remove oldest entry if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey as string);
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }
}

const cellCache = new CellCache();

/**
 * Creates cell configurations for building financials table rows - Simplified Version
 * Shows essential financial metrics without duplicate year columns
 *
 * PERFORMANCE: Implements memoization to prevent unnecessary re-renders
 */
export const createBuildingFinancialsCells = (building: any, context: any) => {
  // Get selection state from context
  const isSelected = context?.isSelected || false;

  // Create cache key: building.id + selection state + hash of relevant fields
  // For non-checkbox cells, selection state doesn't matter for content
  const buildingDataHash = `${building.id}-${building.totalBid}-${building.totalNetRentalIncome}`;
  const cacheKey = `${buildingDataHash}-${isSelected}`;

  // Check cache first
  const cached = cellCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Get sticky column information from context
  const stickyColumnOffsets = context?.stickyColumnOffsets || [];
  const effectiveColumns = context?.effectiveColumns || [];

  // Helper function to get sticky classes and styles for a cell
  const getStickyProps = (colIndex: number) => {
    if (colIndex >= effectiveColumns.length) return {};

    const col = effectiveColumns[colIndex];
    const isSticky = col?.sticky && stickyColumnOffsets[colIndex] !== null;

    if (!isSticky) return {};

    return {
      className: "sticky bg-white z-10",
      style: { left: `${stickyColumnOffsets[colIndex]}px` },
    };
  };

  // Helper function to render currency value with optional click handler
  const renderCurrency = (
    value: number | null | undefined,
    id: string,
    editable: boolean = false,
    eventMode?: string,
  ) => {
    const handleClick =
      editable && eventMode
        ? (e: React.MouseEvent) => {
            e.stopPropagation();
            const selectedIds = context?.selectedRows
              ? Array.from(context.selectedRows)
              : [];
            const buildingIds =
              selectedIds.length > 0 ? selectedIds : [building.id];
            context?.onPickerEvent?.({
              mode: eventMode,
              buildingIds: buildingIds.map(String),
              currentValue: value,
            });
          }
        : undefined;

    return {
      id,
      className: editable
        ? "bg-moss-secondary-light"
        : "bg-neutral-secondary-light",
      content: (
        <div
          className={`flex h-full w-full items-center gap-2 ${editable ? "hover:bg-moss-secondary-light/80 cursor-pointer" : ""}`}
          onClick={handleClick}
        >
          {!editable && (
            <Lock
              size={14}
              weight="fill"
              className="text-neutral-secondary-mid flex-shrink-0"
            />
          )}
          <span
            className={`text-neutral-primary font-medium ${editable ? "hover:text-moss-primary hover:underline" : ""}`}
          >
            {value != null ? applyMask(value, "valuta") : "-"}
          </span>
        </div>
      ),
    };
  };

  // Helper function to render date value with optional click handler
  const renderDate = (
    value: Date | string | null | undefined,
    id: string,
    editable: boolean = false,
    eventMode?: string,
  ) => {
    const handleClick =
      editable && eventMode
        ? (e: React.MouseEvent) => {
            e.stopPropagation();
            const selectedIds = context?.selectedRows
              ? Array.from(context.selectedRows)
              : [];
            const buildingIds =
              selectedIds.length > 0 ? selectedIds : [building.id];
            context?.onPickerEvent?.({
              mode: eventMode,
              buildingIds: buildingIds.map(String),
              currentValue: value,
            });
          }
        : undefined;

    return {
      id,
      className: editable
        ? "bg-moss-secondary-light"
        : "bg-neutral-secondary-light",
      content: (
        <div
          className={`flex h-full w-full items-center gap-2 ${editable ? "hover:bg-moss-secondary-light/80 cursor-pointer" : ""}`}
          onClick={handleClick}
        >
          {!editable && (
            <Lock
              size={14}
              weight="fill"
              className="text-neutral-secondary-mid flex-shrink-0"
            />
          )}
          <span
            className={`text-neutral-primary ${editable ? "hover:text-moss-primary hover:underline" : ""}`}
          >
            {value ? new Date(value).toLocaleDateString("nl-NL") : "-"}
          </span>
        </div>
      ),
    };
  };

  // Checkbox change handler (using isSelected from line 48)
  const handleCheckboxChange = (checked: boolean) => {
    if (context?.onToggleSelect) {
      context.onToggleSelect(checked);
    }
  };

  const cells = [
    // Checkbox - controlled by UniversalTable's selection state (sticky)
    {
      id: "checkbox",
      content: (
        <Checkbox checked={isSelected} onCheckedChange={handleCheckboxChange} />
      ),
      ...getStickyProps(0),
    },

    // ===== BASIC INFO ===== (sticky)
    {
      id: "id",
      content: (
        <Link
          to={`/building/${building.id}/financieel`}
          className="text-neutral-primary hover:text-moss-primary font-medium"
        >
          {building.id}
        </Link>
      ),
      ...getStickyProps(1),
    },
    {
      id: "address",
      content: (
        <Link
          to={`/building/${building.id}/financieel`}
          className="text-neutral-primary hover:text-moss-primary flex items-center gap-2"
        >
          <BuildingApartment weight="duotone" size={16} />
          <span>{building.googlePlaceLongAddress || "-"}</span>
        </Link>
      ),
    },
    {
      id: "company",
      content: building.company?.name ? (
        <span className="text-neutral-primary">{building.company.name}</span>
      ) : (
        <span className="text-neutral-secondary-mid">-</span>
      ),
    },

    // ===== AANKOOP GEGEVENS =====
    renderDate(
      building.acquisitionDate,
      "acquisitionDate",
      true,
      "acquisitionDate",
    ),
    renderCurrency(building.totalBid, "totalBid", true, "totalBid"),
    renderCurrency(
      building.transactionCosts,
      "transactionCosts",
      true,
      "transactionCosts",
    ),
    renderCurrency(
      building.brokerCostsNet,
      "brokerCostsNet",
      true,
      "brokerCostsNet",
    ),
    renderCurrency(
      building.dueDiligenceCost,
      "dueDiligenceCost",
      true,
      "dueDiligenceCost",
    ),
    renderCurrency(
      building.fiscalDiscountCompanyShares,
      "fiscalDiscountCompanyShares",
      true,
      "fiscalDiscountCompanyShares",
    ),
    renderCurrency(
      building.fiscalDiscountRealEstate,
      "fiscalDiscountRealEstate",
      true,
      "fiscalDiscountRealEstate",
    ),
    renderCurrency(
      building.otherDiscounts,
      "otherDiscounts",
      true,
      "otherDiscounts",
    ),
    renderCurrency(
      building.totalCashOutBeforeRenovation,
      "totalCashOutBeforeRenovation",
    ), // Calculated, not editable

    // ===== UITGESTELDE REGISTRATIEBELASTINGEN =====
    renderCurrency(
      building.deferredRegistrationTaxes,
      "deferredRegistrationTaxes",
      true,
      "deferredRegistrationTaxes",
    ),
    renderDate(
      building.deferredRegistrationDate,
      "deferredRegistrationDate",
      true,
      "deferredRegistrationDate",
    ),

    // ===== CAPEX =====
    renderCurrency(
      building.renovationBudget,
      "renovationBudget",
      true,
      "renovationBudget",
    ),
    renderCurrency(building.renovationCostsToDate, "renovationCostsToDate"), // From Odoo, not editable

    // ===== WAARDERING =====
    renderCurrency(
      building.estimatedMarketValue,
      "estimatedMarketValue",
      true,
      "estimatedMarketValue",
    ),
    // Force insuredValue to be editable even when null/undefined
    (() => {
      const value = building.insuredValue;
      const id = "insuredValue";
      const eventMode = "insuredValue";

      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log("🔵 insuredValue clicked with value:", value);
        const selectedIds = context?.selectedRows
          ? Array.from(context.selectedRows)
          : [];
        const buildingIds =
          selectedIds.length > 0 ? selectedIds : [building.id];
        console.log("🔵 Building IDs:", buildingIds);
        context?.onPickerEvent?.({
          mode: eventMode,
          buildingIds: buildingIds.map(String),
          currentValue: value,
        });
      };

      return {
        id,
        className: "bg-moss-secondary-light",
        content: (
          <div
            className="hover:bg-moss-secondary-light/80 flex h-full w-full cursor-pointer items-center gap-2"
            onClick={handleClick}
          >
            <span className="text-neutral-primary hover:text-moss-primary font-medium hover:underline">
              {value != null ? applyMask(value, "valuta") : "-"}
            </span>
          </div>
        ),
      };
    })(),

    // ===== HUURINKOMSTEN =====
    renderCurrency(building.actualIndexedContractualRentsResidential, "actualIndexedContractualRentsResidential"),
    renderCurrency(building.actualResidentialRentInTerminationPhase, "actualResidentialRentInTerminationPhase"),
    renderCurrency(building.actualIndexedContractualRentsRetail, "actualIndexedContractualRentsRetail"),
    renderCurrency(building.actualRetailRentInTerminationPhase, "actualRetailRentInTerminationPhase"),
    renderCurrency(building.actualIndexedContractualRentsOffices, "actualIndexedContractualRentsOffices"),
    renderCurrency(building.actualOfficeRentInTerminationPhase, "actualOfficeRentInTerminationPhase"),
    renderCurrency(building.actualIndexedContractualRentsTotal, "actualIndexedContractualRentsTotal"),
    renderCurrency(building.actualTotalRentInTerminationPhase, "actualTotalRentInTerminationPhase"),
    renderCurrency(building.ervOnVacantUnits, "ervOnVacantUnits"),
    renderCurrency(building.contractualRentsPlusErv, "contractualRentsPlusErv"),
    renderCurrency(building.potentialGrossIncomeActual, "potentialGrossIncomeActual"),
    renderCurrency(building.deltaRentalValueSinceAcquisition, "deltaRentalValueSinceAcquisition"),
    {
      id: "actualVacantUnits",
      className: "bg-neutral-secondary-light",
      content: (
        <div className="flex h-full w-full items-center gap-2">
          <Lock
            size={14}
            weight="fill"
            className="text-neutral-secondary-mid flex-shrink-0"
          />
          <span className="text-neutral-primary">
            {building.actualVacantUnits || "-"}
          </span>
        </div>
      ),
    },
    renderCurrency(building.ervTotal, "ervTotal"),
    renderCurrency(building.ervResidentialUnits, "ervResidentialUnits"),
    renderCurrency(building.ervRetailUnits, "ervRetailUnits"),
    renderCurrency(building.ervOfficeUnits, "ervOfficeUnits"),

    // ===== OPERATIONEEL (Dit jaar) =====
    renderCurrency(building.totalNetRentalIncome, "totalNetRentalIncome"),
    renderCurrency(building.totalRentalIncome, "totalRentalIncome"),
    renderCurrency(building.totalProvisions, "totalProvisions"),
    renderCurrency(building.totalRentExpenditures, "totalRentExpenditures"),
    renderCurrency(building.totalTaxProvisions, "totalTaxProvisions"),
    renderCurrency(building.totalTaxExpenditures, "totalTaxExpenditures"),
    renderCurrency(building.propertyCosts, "propertyCosts"),
    renderCurrency(building.marginBeforeVAT, "marginBeforeVAT"),
    // Render percentage with special formatting
    {
      id: "percentageOperationalCosts",
      className: "bg-neutral-secondary-light",
      content: (
        <div className="flex h-full w-full items-center gap-2">
          <Lock
            size={14}
            weight="fill"
            className="text-neutral-secondary-mid flex-shrink-0"
          />
          <span className="text-neutral-primary font-medium">
            {building.percentageOperationalCosts != null &&
            typeof building.percentageOperationalCosts === "number" &&
            !isNaN(building.percentageOperationalCosts)
              ? `${building.percentageOperationalCosts.toFixed(2)}%`
              : "-"}
          </span>
        </div>
      ),
    },
  ];

  // Store in cache before returning
  cellCache.set(cacheKey, cells);

  return cells;
};

/**
 * Clear the cell cache (useful when data is refreshed)
 */
export const clearCellCache = () => {
  cellCache.clear();
};
